/* Danachi 2026 - ORBIT.
   A drag-to-spin 3D showroom. Real products hang in the void as lit panels;
   grab and throw the ring, hover to raise a piece, click to open it.
   Without WebGL the section degrades to the plain product list in the DOM. */
import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.160.0/build/three.module.js';

function mountOrbit(root) {
  var canvas = root.querySelector('[data-orb-canvas]');
  var links = Array.prototype.slice.call(root.querySelectorAll('[data-orb-item]'));
  if (!canvas || !links.length) return null;

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var mobile = window.matchMedia('(max-width: 749px)').matches;

  var crimson = new THREE.Color(root.dataset.crimson || '#c1121f');
  var violet = new THREE.Color(root.dataset.violet || '#7b5cff');

  var items = links.map(function (a) {
    return {
      el: a,
      url: a.getAttribute('href'),
      img: a.dataset.img,
      title: a.dataset.title || '',
      price: a.dataset.price || ''
    };
  });

  var hudTitle = root.querySelector('[data-orb-title]');
  var hudPrice = root.querySelector('[data-orb-price]');
  var hudCta = root.querySelector('[data-orb-cta]');
  var btnPrev = root.querySelector('[data-orb-prev]');
  var btnNext = root.querySelector('[data-orb-next]');

  var renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true, powerPreference: 'high-performance' });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, mobile ? 1.5 : 2));
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.12;
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  var scene = new THREE.Scene();
  scene.fog = new THREE.FogExp2(0x05030a, 0.032);

  var camera = new THREE.PerspectiveCamera(42, 1, 0.1, 200);

  var N = items.length;
  var radius = Math.max(4.6, N * 0.72);
  var CARD_W = 2.15;
  var CARD_H = 2.95;

  camera.position.set(0, 0.55, radius + 6.4);
  camera.lookAt(0, -0.15, 0);

  var junk = { geo: [], mat: [], tex: [] };

  var ring = new THREE.Group();
  scene.add(ring);

  var loader = new THREE.TextureLoader();
  loader.setCrossOrigin('anonymous');

  var cardGeo = new THREE.PlaneGeometry(CARD_W, CARD_H, 1, 1);
  junk.geo.push(cardGeo);

  /* soft crimson halo that sits behind each panel */
  var haloTex = (function () {
    var c = document.createElement('canvas');
    c.width = c.height = 128;
    var g = c.getContext('2d');
    var grd = g.createRadialGradient(64, 64, 0, 64, 64, 64);
    grd.addColorStop(0, 'rgba(255,255,255,0.5)');
    grd.addColorStop(0.4, 'rgba(255,255,255,0.14)');
    grd.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grd;
    g.fillRect(0, 0, 128, 128);
    var t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.SRGBColorSpace;
    return t;
  })();
  junk.tex.push(haloTex);

  var cards = [];

  for (var i = 0; i < N; i++) {
    var ang = (i / N) * Math.PI * 2;
    var pivot = new THREE.Group();
    pivot.rotation.y = ang;

    var holder = new THREE.Group();
    holder.position.z = radius;

    var mat = new THREE.MeshBasicMaterial({
      color: 0x8d8d95,
      transparent: true,
      side: THREE.DoubleSide,
      toneMapped: false
    });
    junk.mat.push(mat);

    var mesh = new THREE.Mesh(cardGeo, mat);
    mesh.userData.index = i;
    holder.add(mesh);

    /* halo */
    var halo = new THREE.Mesh(cardGeo, new THREE.MeshBasicMaterial({
      map: haloTex,
      color: crimson,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false
    }));
    junk.mat.push(halo.material);
    halo.scale.set(1.5, 1.4, 1);
    halo.position.z = -0.05;
    holder.add(halo);

    /* mirrored reflection under the floor line */
    var refl = new THREE.Mesh(cardGeo, new THREE.MeshBasicMaterial({
      color: 0x6a6a72,
      transparent: true,
      opacity: 0.16,
      side: THREE.DoubleSide,
      depthWrite: false,
      toneMapped: false
    }));
    junk.mat.push(refl.material);
    refl.scale.y = -1;
    refl.position.y = -(CARD_H + 0.16);
    holder.add(refl);

    pivot.add(holder);
    ring.add(pivot);

    cards.push({ pivot: pivot, holder: holder, mesh: mesh, halo: halo, refl: refl, mat: mat, reflMat: refl.material, index: i });

    /* load the artwork */
    (function (card, url) {
      if (!url) return;
      loader.load(
        url,
        function (tex) {
          tex.colorSpace = THREE.SRGBColorSpace;
          tex.anisotropy = 4;
          junk.tex.push(tex);
          card.mat.map = tex;
          card.mat.color.set(0xffffff);
          card.mat.needsUpdate = true;
          card.reflMat.map = tex;
          card.reflMat.color.set(0xffffff);
          card.reflMat.needsUpdate = true;
        },
        undefined,
        function () { /* leave the flat panel colour in place */ }
      );
    })(cards[i], items[i].img);
  }

  /* ---- floor glow ---- */
  var floorTex = (function () {
    var c = document.createElement('canvas');
    c.width = c.height = 256;
    var g = c.getContext('2d');
    var grd = g.createRadialGradient(128, 128, 0, 128, 128, 128);
    grd.addColorStop(0, 'rgba(193,18,31,0.5)');
    grd.addColorStop(0.45, 'rgba(123,92,255,0.14)');
    grd.addColorStop(1, 'rgba(0,0,0,0)');
    g.fillStyle = grd;
    g.fillRect(0, 0, 256, 256);
    var t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.SRGBColorSpace;
    return t;
  })();
  junk.tex.push(floorTex);

  var floorGeo = new THREE.PlaneGeometry(radius * 4.2, radius * 4.2);
  junk.geo.push(floorGeo);
  var floorMat = new THREE.MeshBasicMaterial({
    map: floorTex,
    transparent: true,
    opacity: 0.75,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    toneMapped: false
  });
  junk.mat.push(floorMat);
  var floor = new THREE.Mesh(floorGeo, floorMat);
  floor.rotation.x = -Math.PI / 2;
  floor.position.y = -(CARD_H / 2 + 0.08);
  scene.add(floor);

  /* ---- dust ---- */
  var dustCount = mobile ? 140 : 340;
  var dp = new Float32Array(dustCount * 3);
  var ds = new Float32Array(dustCount);
  for (var d = 0; d < dustCount; d++) {
    dp[d * 3] = (Math.random() - 0.5) * radius * 4;
    dp[d * 3 + 1] = (Math.random() - 0.5) * 9;
    dp[d * 3 + 2] = (Math.random() - 0.5) * radius * 4;
    ds[d] = Math.random();
  }
  var dustGeo = new THREE.BufferGeometry();
  dustGeo.setAttribute('position', new THREE.BufferAttribute(dp, 3));
  junk.geo.push(dustGeo);
  var dustMat = new THREE.PointsMaterial({
    color: violet,
    size: 0.045,
    transparent: true,
    opacity: 0.6,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    fog: false
  });
  junk.mat.push(dustMat);
  var dust = new THREE.Points(dustGeo, dustMat);
  scene.add(dust);

  /* ---------------- interaction ---------------- */
  var meshes = cards.map(function (c) { return c.mesh; });

  var step = (Math.PI * 2) / N;
  var angle = 0;          /* current ring rotation, unbounded - it winds up */
  var targetAngle = 0;    /* where it wants to settle */
  var wantIndex = 0;      /* committed target, also unbounded */
  var velocity = 0;
  var dragging = false;
  var dragged = false;
  var lastX = 0;
  var focus = 0;
  var pointerNDC = new THREE.Vector2(-2, -2);
  var raycaster = new THREE.Raycaster();
  var hovered = -1;

  function setFocus(n) {
    var idx = ((n % N) + N) % N;
    if (idx === focus) return;
    focus = idx;
    paintHud();
  }

  function paintHud() {
    var it = items[focus];
    if (!it) return;
    if (hudTitle) hudTitle.textContent = it.title;
    if (hudPrice) hudPrice.textContent = it.price;
    if (hudCta) hudCta.setAttribute('href', it.url);
    root.setAttribute('data-orb-focus', String(focus));
  }
  paintHud();

  function goTo(n) {
    wantIndex = n;
    targetAngle = -n * step;
    velocity = 0;
  }

  function nearestIndex() {
    return Math.round(-angle / step);
  }

  /* angle winds up without bound, but card indices are 0..N-1. Feeding a raw
     index to goTo would unwind the whole ring; pick the equivalent index
     closest to where we currently are instead. */
  function goToNearest(idx) {
    var cur = nearestIndex();
    var d = (((idx - cur) % N) + N) % N;
    if (d > N / 2) d -= N;
    goTo(cur + d);
  }

  function settle() {
    wantIndex = Math.round(-angle / step);
    targetAngle = -wantIndex * step;
  }

  /* pick from a live event rather than from state the render loop maintains -
     no frame runs between pointerup and click, so a cached hover is stale */
  function pickAt(e) {
    var r = canvas.getBoundingClientRect();
    var v = new THREE.Vector2(
      ((e.clientX - r.left) / r.width) * 2 - 1,
      -((e.clientY - r.top) / r.height) * 2 + 1
    );
    raycaster.setFromCamera(v, camera);
    var hit = raycaster.intersectObjects(meshes, false);
    return hit.length ? hit[0].object.userData.index : -1;
  }

  function onDown(e) {
    dragging = true;
    dragged = false;
    lastX = e.clientX;
    velocity = 0;
    root.classList.add('is-grabbing');
    if (canvas.setPointerCapture && e.pointerId !== undefined) {
      try { canvas.setPointerCapture(e.pointerId); } catch (err) {}
    }
  }

  function onMove(e) {
    var r = canvas.getBoundingClientRect();
    pointerNDC.x = ((e.clientX - r.left) / r.width) * 2 - 1;
    pointerNDC.y = -((e.clientY - r.top) / r.height) * 2 + 1;

    if (!dragging) return;
    var dx = e.clientX - lastX;
    lastX = e.clientX;
    if (Math.abs(dx) > 2) dragged = true;
    var k = 0.0055 * (radius / 5);
    angle += dx * k;
    velocity = dx * k;
  }

  function onUp() {
    if (!dragging) return;
    dragging = false;
    root.classList.remove('is-grabbing');
    /* throw, then settle on the nearest piece */
    wantIndex = Math.round(-(angle + velocity * 9) / step);
    targetAngle = -wantIndex * step;
  }

  function onClick(e) {
    if (dragged) return;
    var idx = pickAt(e);
    if (idx < 0) return;
    if (idx === focus) window.location.href = items[idx].url;
    else goToNearest(idx);
  }

  function onWheel(e) {
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
      e.preventDefault();
      angle += e.deltaX * -0.0012;
      settle();
    }
  }

  /* step from the committed target, not from the lagging angle - otherwise
     rapid presses re-derive the same index and get swallowed */
  function onKey(e) {
    if (e.key === 'ArrowLeft') { goTo(wantIndex - 1); e.preventDefault(); }
    else if (e.key === 'ArrowRight') { goTo(wantIndex + 1); e.preventDefault(); }
    else if (e.key === 'Enter') {
      window.location.href = items[focus].url;
    }
  }

  canvas.addEventListener('pointerdown', onDown);
  canvas.addEventListener('pointermove', onMove);
  window.addEventListener('pointerup', onUp);
  canvas.addEventListener('pointerleave', function () { pointerNDC.set(-2, -2); });
  canvas.addEventListener('click', onClick);
  canvas.addEventListener('wheel', onWheel, { passive: false });
  canvas.addEventListener('keydown', onKey);

  if (btnPrev) btnPrev.addEventListener('click', function () { goTo(wantIndex - 1); });
  if (btnNext) btnNext.addEventListener('click', function () { goTo(wantIndex + 1); });

  function resize() {
    var w = root.clientWidth;
    var h = root.clientHeight;
    if (!w || !h) return;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }
  var ro = new ResizeObserver(resize);
  ro.observe(root);
  resize();

  var onScreen = true;
  var io = new IntersectionObserver(function (entries) {
    onScreen = entries[0].isIntersecting;
  }, { threshold: 0.02 });
  io.observe(root);

  var clock = new THREE.Clock();
  var raf = 0;
  var disposed = false;

  function frame() {
    if (disposed) return;
    raf = requestAnimationFrame(frame);
    if (!onScreen) return;

    var t = clock.getElapsedTime();

    if (!dragging) {
      if (Math.abs(velocity) > 0.0002) {
        angle += velocity;
        velocity *= 0.94;
        settle();
      } else {
        angle += (targetAngle - angle) * 0.085;
      }
    }
    ring.rotation.y = angle;

    setFocus(nearestIndex());

    /* only drop the hover once a real drag begins - merely holding the button
       down must not clear it, or the following click has nothing to act on */
    if (dragging && dragged) {
      hovered = -1;
    } else if (pointerNDC.x > -1.5) {
      raycaster.setFromCamera(pointerNDC, camera);
      var hit = raycaster.intersectObjects(meshes, false);
      hovered = hit.length ? hit[0].object.userData.index : -1;
    }
    root.classList.toggle('is-pointing', hovered >= 0);

    /* per-card presentation: the piece facing you is lifted and lit */
    for (var i = 0; i < cards.length; i++) {
      var c = cards[i];
      var world = (i / N) * Math.PI * 2 + angle;
      var facing = Math.cos(world); /* 1 when it is right in front of the camera */
      var front = Math.max(0, facing);

      var isFocus = i === focus;
      var isHover = i === hovered;
      var lift = front * 0.34 + (isHover ? 0.22 : 0) + (isFocus ? 0.16 : 0);
      var bob = reduced ? 0 : Math.sin(t * 0.9 + i * 0.7) * 0.05;

      c.holder.position.y += ((lift + bob) - c.holder.position.y) * 0.12;

      var scale = 1 + front * 0.1 + (isHover ? 0.09 : 0) + (isFocus ? 0.05 : 0);
      c.holder.scale.x += (scale - c.holder.scale.x) * 0.12;
      c.holder.scale.y = c.holder.scale.x;

      var op = 0.24 + front * 0.76;
      c.mat.opacity += (op - c.mat.opacity) * 0.12;
      c.reflMat.opacity += (op * 0.17 - c.reflMat.opacity) * 0.12;

      var haloOp = (isHover ? 0.7 : 0) + front * 0.3;
      c.halo.material.opacity += (haloOp - c.halo.material.opacity) * 0.14;

      /* always face the viewer rather than sitting flat on the ring */
      c.holder.rotation.y = -world;
    }

    if (!reduced) {
      var dpos = dustGeo.attributes.position;
      for (var q = 0; q < dustCount; q++) {
        var y = dpos.array[q * 3 + 1] + 0.0016 + ds[q] * 0.0032;
        if (y > 4.5) y = -4.5;
        dpos.array[q * 3 + 1] = y;
      }
      dpos.needsUpdate = true;
      floorMat.opacity = 0.62 + Math.sin(t * 1.1) * 0.12;
    }

    renderer.render(scene, camera);
  }
  frame();

  root.classList.add('is-3d');

  return function dispose() {
    if (disposed) return;
    disposed = true;
    cancelAnimationFrame(raf);
    ro.disconnect();
    io.disconnect();
    canvas.removeEventListener('pointerdown', onDown);
    canvas.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
    canvas.removeEventListener('click', onClick);
    canvas.removeEventListener('wheel', onWheel);
    canvas.removeEventListener('keydown', onKey);
    root.classList.remove('is-3d');
    for (var a = 0; a < junk.geo.length; a++) junk.geo[a].dispose();
    for (var b = 0; b < junk.mat.length; b++) junk.mat[b].dispose();
    for (var c2 = 0; c2 < junk.tex.length; c2++) junk.tex[c2].dispose();
    renderer.dispose();
    if (renderer.forceContextLoss) renderer.forceContextLoss();
  };
}

function mount(el) {
  if (el.__orbDispose) return;
  try {
    el.__orbDispose = mountOrbit(el) || null;
  } catch (err) {
    el.classList.add('is-fallback');
    console.warn('[danachi-orbit]', err);
  }
}

function unmount(el) {
  if (el && typeof el.__orbDispose === 'function') {
    el.__orbDispose();
    el.__orbDispose = null;
  }
}

function boot(scope) {
  (scope || document).querySelectorAll('[data-danachi-orbit]').forEach(mount);
}

if (document.readyState !== 'loading') boot();
else document.addEventListener('DOMContentLoaded', function () { boot(); });

document.addEventListener('shopify:section:unload', function (e) {
  if (!e.target || !e.target.querySelectorAll) return;
  e.target.querySelectorAll('[data-danachi-orbit]').forEach(unmount);
});
document.addEventListener('shopify:section:load', function (e) {
  boot(e.target && e.target.querySelectorAll ? e.target : document);
});
/* e.persisted means the page is going into the back/forward cache and will be
   restored intact. Tearing down here would leave a dead canvas and a reverted
   is-3d class on Back, and nothing remounts because DOMContentLoaded does not
   fire again. WebGL does not block bfcache and the browser suspends rAF for us. */
window.addEventListener('pagehide', function (e) {
  if (e.persisted) return;
  document.querySelectorAll('[data-danachi-orbit]').forEach(unmount);
});
